// AdminEmailTemplates.js — Pré-formatação do email de envio de propostas
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Mail, Save, RotateCcw, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLACEHOLDERS = [
  { k: "primeiro_nome", desc: "Primeiro nome do candidato" },
  { k: "empresa", desc: "Nome da empresa" },
  { k: "link", desc: "Link da proposta" },
  { k: "remetente_nome", desc: "Nome do utilizador que envia" },
  { k: "remetente_email", desc: "Email do remetente" },
  { k: "remetente_phone_line", desc: "Linha com telefone do remetente (se preenchido)" },
];

const AdminEmailTemplates = () => {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tpl, setTpl] = useState({ subject: "", body_html: "", body_text: "" });

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/email-templates/proposal`, { headers: getAuthHeaders() });
      setTpl({
        subject: r.data.subject || "",
        body_html: r.data.body_html || "",
        body_text: r.data.body_text || "",
      });
    } catch {
      toast.error("Erro a carregar template");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/email-templates/proposal`, tpl, { headers: getAuthHeaders() });
      toast.success("Template do email atualizado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a guardar");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 font-sans">
        <Loader2 className="w-8 h-8 animate-spin text-brand-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 font-sans" data-testid="admin-email-templates">
      <div className="container mx-auto px-4 py-6 max-w-4xl">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <Link to="/admin">
              <Button variant="outline" size="sm" className="border-slate-300">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-brand-600 flex items-center gap-2">
                <Mail className="w-6 h-6" />
                Template Email Proposta
              </h1>
              <p className="text-sm text-slate-500">
                Pré-formatação do email enviado quando partilha o link/PDF da proposta.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={load}
              variant="outline"
              size="sm"
              className="border-slate-300"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Recarregar
            </Button>
            <Button
              onClick={save}
              size="sm"
              className="bg-brand-600 hover:bg-brand-700 text-white"
              disabled={saving}
              data-testid="save-template-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Guardar
            </Button>
          </div>
        </div>

        <Card className="bg-white mb-3">
          <CardContent className="p-4 space-y-3">
            <div>
              <Label className="text-xs">Assunto</Label>
              <Input
                value={tpl.subject}
                onChange={(e) => setTpl({ ...tpl, subject: e.target.value })}
                className="mt-1"
                data-testid="email-subject-input"
              />
            </div>
            <div>
              <Label className="text-xs">Corpo HTML</Label>
              <Textarea
                value={tpl.body_html}
                onChange={(e) => setTpl({ ...tpl, body_html: e.target.value })}
                className="mt-1 min-h-[180px] font-mono text-xs"
                data-testid="email-body-html-input"
              />
            </div>
            <div>
              <Label className="text-xs">Corpo texto (fallback p/ clientes sem HTML)</Label>
              <Textarea
                value={tpl.body_text}
                onChange={(e) => setTpl({ ...tpl, body_text: e.target.value })}
                className="mt-1 min-h-[120px] font-mono text-xs"
                data-testid="email-body-text-input"
              />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-amber-50 border-amber-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-amber-800">Placeholders disponíveis</CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-1 text-xs">
            {PLACEHOLDERS.map((p) => (
              <div key={p.k} className="flex items-baseline gap-2">
                <code className="bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded font-mono">{"{" + p.k + "}"}</code>
                <span className="text-amber-900">{p.desc}</span>
              </div>
            ))}
            <p className="text-[11px] text-amber-700 mt-2 italic">
              Nota: O envio está atualmente em modo <strong>MOCKED</strong> — quando integrar SendGrid o template será usado directamente.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminEmailTemplates;
